import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import { CheckCircleOutlined, CloseOutlined } from '@ant-design/icons';
import ImportBrokerCSVFlyin from 'components/flyins/ImportBrokerCSVFlyin';
import IbkrFlexConnectionFlyin from 'components/flyins/IbkrFlexConnectionFlyin';
import DataPrivacyFlyin from 'components/flyins/DataPrivacyFlyin';

export const QUICK_START_KEYS = ['basicProfile', 'connectBroker', 'riskProfile', 'tradingGoal', 'preferredMarket', 'dataPrivacy'];

const INITIAL_STATE = Object.fromEntries(QUICK_START_KEYS.map((key) => [key, false]));

export const readQuickStartState = () => {
  try {
    return { ...INITIAL_STATE, ...JSON.parse(localStorage.getItem('quickStartStatus') || '{}') };
  } catch {
    return INITIAL_STATE;
  }
};

const TASKS = [
  { key: 'basicProfile', title: 'Basic Profile Setup', description: 'Complete your personal and account information.', action: 'Open profile', path: '/view-profile?tab=user-info' },
  { key: 'connectBroker', title: 'Connect to Broker', description: 'Connect IBKR or import your broker history.', action: 'Manage IBKR', flyin: 'flexConnect', secondaryAction: 'Import CSV', secondaryFlyin: 'importBroker' },
  { key: 'riskProfile', title: 'Risk Profile Setup', description: 'Set the limits and risk tolerance that guide each trade.', action: 'Set risk profile', path: '/view-profile?tab=trader-info' },
  { key: 'tradingGoal', title: 'Define Trading Goal', description: 'Choose the outcome and time horizon you are trading toward.', action: 'Set trading goal', path: '/view-profile?tab=trader-info' },
  { key: 'preferredMarket', title: 'Select Preferred Market', description: 'Choose the markets and instruments you want to trade.', action: 'Select markets', path: '/view-profile?tab=trader-info' },
  { key: 'dataPrivacy', title: 'Data Privacy Preferences', description: 'Review your privacy settings and consent preferences.', action: 'Review & consent', flyin: 'dataPrivacy' }
];

export default function QuickStartWizard({ open, onClose, onNavigate, onProgressChange }) {
  const [status, setStatus] = React.useState(readQuickStartState);
  const [flyins, setFlyins] = React.useState({ importBroker: false, flexConnect: false, dataPrivacy: false });

  React.useEffect(() => {
    if (open) setStatus(readQuickStartState());
  }, [open]);

  const updateTask = (key, value = true) => {
    setStatus((current) => {
      const next = { ...current, [key]: value };
      const completed = QUICK_START_KEYS.filter((taskKey) => next[taskKey]).length;
      const saved = { ...next, progress: Math.round((completed / QUICK_START_KEYS.length) * 100) };
      localStorage.setItem('quickStartStatus', JSON.stringify(saved));
      onProgressChange(saved);
      return saved;
    });
  };

  const completed = QUICK_START_KEYS.filter((key) => status[key]).length;
  const progress = Math.round((completed / QUICK_START_KEYS.length) * 100);
  const runAction = (task, secondary = false) => {
    const flyin = secondary ? task.secondaryFlyin : task.flyin;
    const path = secondary ? task.secondaryPath : task.path;
    if (flyin) {
      setFlyins((current) => ({ ...current, [flyin]: true }));
      return;
    }
    updateTask(task.key);
    onClose();
    if (path) onNavigate(path);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
        aria-labelledby="quick-start-title"
        PaperProps={{ sx: { bgcolor: 'background.paper', color: 'text.primary', backgroundImage: 'none' } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid var(--app-border, #eef0f3)', pr: 7 }}>
          <Typography id="quick-start-title" variant="h4">Quick Start</Typography>
          <Typography color="text.secondary" variant="body2">Walk through the essentials to get Tradatomy ready for your trading process.</Typography>
          <IconButton aria-label="Close Quick Start" onClick={onClose} sx={{ position: 'absolute', right: 14, top: 14 }}><CloseOutlined /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography fontWeight={700}>{completed} of {QUICK_START_KEYS.length} complete</Typography>
            <Typography color="primary" fontWeight={700}>{progress}%</Typography>
          </Stack>
          <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 8, mb: 3 }} />
          <Stack spacing={1.5}>
            {TASKS.map((task) => (
              <Box key={task.key} data-testid={`quick-start-${task.key}`} sx={{ p: 2, border: '1px solid var(--app-border, #e4e8ee)', borderRadius: '14px', display: 'flex', gap: 2, alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' } }}>
                <CheckCircleOutlined style={{ color: status[task.key] ? '#2fb36d' : 'var(--app-muted)', fontSize: 20 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={700}>{task.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{task.description}</Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  {task.secondaryAction ? <Button size="small" variant="text" onClick={() => runAction(task, true)}>{task.secondaryAction}</Button> : null}
                  <Button size="small" variant={status[task.key] ? 'outlined' : 'contained'} onClick={() => runAction(task)}>{status[task.key] ? 'Review' : task.action}</Button>
                </Stack>
              </Box>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
      <ImportBrokerCSVFlyin open={flyins.importBroker} onClose={() => setFlyins((current) => ({ ...current, importBroker: false }))} onSuccess={() => updateTask('connectBroker')} />
      <IbkrFlexConnectionFlyin open={flyins.flexConnect} onClose={() => setFlyins((current) => ({ ...current, flexConnect: false }))} onConnected={() => updateTask('connectBroker')} onOpenCsvImport={() => setFlyins((current) => ({ ...current, importBroker: true }))} />
      <DataPrivacyFlyin open={flyins.dataPrivacy} onClose={() => setFlyins((current) => ({ ...current, dataPrivacy: false }))} onConsent={() => updateTask('dataPrivacy')} />
    </>
  );
}

QuickStartWizard.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onProgressChange: PropTypes.func.isRequired
};
